import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export interface MealPlan {
  id: string;
  project_id: string;
  person_id: string;
  plan_date: string;
  lunch: boolean;
  dinner: boolean;
  created_at: string;
  updated_at: string;
}

export interface HotelPlan {
  id: string;
  project_id: string;
  person_id: string;
  plan_date: string;
  needs_hotel: boolean;
  room_type: "single" | "double";
  roommate_person_id: string | null;
  created_at: string;
  updated_at: string;
}

type ZeroMealPlan = {
  id: string;
  projectID: string;
  personID: string;
  planDate: number | string;
  lunch: boolean;
  dinner: boolean;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroHotelPlan = {
  id: string;
  projectID: string;
  personID: string;
  planDate: number | string;
  needsHotel: boolean;
  roomType?: "single" | "double" | null;
  roommatePersonID?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

function toLegacyDate(value: number | string) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value.slice(0, 10);
}

function toLegacyMealPlan(row: ZeroMealPlan): MealPlan {
  return {
    id: row.id,
    project_id: row.projectID,
    person_id: row.personID,
    plan_date: toLegacyDate(row.planDate),
    lunch: row.lunch,
    dinner: row.dinner,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toLegacyHotelPlan(row: ZeroHotelPlan): HotelPlan {
  return {
    id: row.id,
    project_id: row.projectID,
    person_id: row.personID,
    plan_date: toLegacyDate(row.planDate),
    needs_hotel: row.needsHotel,
    room_type: row.roomType ?? "single",
    roommate_person_id: row.roommatePersonID ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

type HotelPlanInput = {
  project_id: string;
  person_id: string;
  plan_date: string;
  needs_hotel: boolean;
  room_type?: "single" | "double";
  roommate_person_id?: string | null;
};

export function useProjectMealPlans(projectId: string | undefined) {
  const query = projectId
    ? appQueries.projectMealPlans.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });
  const plans = ((data ?? []) as ZeroMealPlan[]).map(toLegacyMealPlan);
  return {
    data: plans,
    isLoading: !!projectId && details.type !== "complete" && plans.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useProjectHotelPlans(projectId: string | undefined) {
  const query = projectId
    ? appQueries.projectHotelPlans.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });
  const plans = ((data ?? []) as ZeroHotelPlan[]).map(toLegacyHotelPlan);
  return {
    data: plans,
    isLoading: !!projectId && details.type !== "complete" && plans.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useUpsertMealPlan() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (plan: {
    project_id: string;
    person_id: string;
    plan_date: string;
    lunch: boolean;
    dinner: boolean;
  }) =>
    runPending(async () => {
      const input = {
        id: generateUUID(),
        projectID: plan.project_id,
        personID: plan.person_id,
        planDate: plan.plan_date,
        lunch: plan.lunch,
        dinner: plan.dinner,
      };
      const mutation = zero.mutate(appMutators.projectMealPlans.upsert(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Meal plan update was rejected",
      );
      return toLegacyMealPlan(input);
    });
  return {
    mutate: (
      plan: {
        project_id: string;
        person_id: string;
        plan_date: string;
        lunch: boolean;
        dinner: boolean;
      },
      options?: MutationOptions<MealPlan>,
    ) => {
      void run(plan)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update meal plan: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpsertHotelPlan() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (plan: HotelPlanInput) =>
    runPending(async () => {
      const input = {
        id: generateUUID(),
        projectID: plan.project_id,
        personID: plan.person_id,
        planDate: plan.plan_date,
        needsHotel: plan.needs_hotel,
        roomType: plan.room_type ?? "single",
        roommatePersonID:
          plan.needs_hotel && plan.room_type === "double"
            ? (plan.roommate_person_id ?? null)
            : null,
      };
      const mutation = zero.mutate(appMutators.projectHotelPlans.upsert(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Hotel plan update was rejected",
      );
      return toLegacyHotelPlan(input);
    });
  return {
    mutate: (plan: HotelPlanInput, options?: MutationOptions<HotelPlan>) => {
      void run(plan)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update hotel plan: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useBatchUpsertMealPlans() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({
    projectId,
    plans,
  }: {
    projectId: string;
    plans: {
      project_id: string;
      person_id: string;
      plan_date: string;
      lunch: boolean;
      dinner: boolean;
    }[];
  }) =>
    runPending(async () => {
      const input = {
        projectID: projectId,
        plans: plans.map((plan) => ({
          id: generateUUID(),
          projectID: projectId,
          personID: plan.person_id,
          planDate: plan.plan_date,
          lunch: plan.lunch,
          dinner: plan.dinner,
        })),
      };
      const mutation = zero.mutate(
        appMutators.projectMealPlans.batchUpsert(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Meal plan batch update was rejected",
      );
      return input.plans.map(toLegacyMealPlan);
    });
  return {
    mutate: (
      input: {
        projectId: string;
        plans: {
          project_id: string;
          person_id: string;
          plan_date: string;
          lunch: boolean;
          dinner: boolean;
        }[];
      },
      options?: MutationOptions<MealPlan[]>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update meal plans: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useBatchUpsertHotelPlans() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({
    projectId,
    plans,
  }: {
    projectId: string;
    plans: HotelPlanInput[];
  }) =>
    runPending(async () => {
      const input = {
        projectID: projectId,
        plans: plans.map((plan) => ({
          id: generateUUID(),
          projectID: projectId,
          personID: plan.person_id,
          planDate: plan.plan_date,
          needsHotel: plan.needs_hotel,
          roomType: plan.room_type ?? "single",
          roommatePersonID:
            plan.needs_hotel && plan.room_type === "double"
              ? (plan.roommate_person_id ?? null)
              : null,
        })),
      };
      const mutation = zero.mutate(
        appMutators.projectHotelPlans.batchUpsert(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Hotel plan batch update was rejected",
      );
      return input.plans.map(toLegacyHotelPlan);
    });
  return {
    mutate: (
      input: {
        projectId: string;
        plans: HotelPlanInput[];
      },
      options?: MutationOptions<HotelPlan[]>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update hotel plans: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
